// ProctoringMount — reusable browser-side proctoring bundle.
//
// Drop this into ANY test component to get the exact same proctoring that
// coding (iCode/QuickCodingTest) and aptitude (AptitudeTest) already run:
//   • MediaPipe detectors (face presence / multiple faces / head pose /
//     eye gaze / hands) via <ProctoringEngine>
//   • Server-side webcam snapshots as tamper-proof evidence via <SnapshotEngine>
// Both engines POST to the existing /api/cheating/* endpoints keyed by testId,
// so the recruiter-side evidence appears the same way it does for coding/apt.
//
// Why a wrapper (good engineering): the two engines + webcam handling were
// otherwise copy-pasted per test type. Centralising it means each test
// component adds ONE line, and any future change (a new detector, a flag)
// lands in one place.
//
// Stream handling — deliberately safe so it never breaks an existing flow:
//   • If a `stream` WITH a live video track is passed in, we REUSE it and do
//     NOT open a second camera. This matters for components that already hold
//     the webcam (e.g. QuestionSection's face recording) — opening a 2nd
//     getUserMedia on the same device can throw NotReadableError.
//   • If no usable video stream is passed (e.g. RealTimeInterview, which only
//     opens an audio stream), we open our OWN video-only stream and stop it on
//     unmount.
//   • If the camera is denied/unavailable, both engines no-op gracefully — the
//     candidate is never blocked from taking the test.
//
// Feature flags stay authoritative: the engines only start detectors that are
// enabled globally (the same flags coding/aptitude already use), so mounting
// this does nothing extra unless proctoring is switched on.

import React, { useEffect, useRef, useState } from 'react';
import ProctoringEngine from '../Coding/Proctoring/ProctoringEngine';
import SnapshotEngine from '../Coding/Snapshots/SnapshotEngine';

export default function ProctoringMount({
  testId,
  stream = null,
  active = true,
  applicationId = null,
  candidateEmail = null,
  // When false, NEVER open our own camera — only ever use the `stream` the
  // caller passes. Use this for components that already own the webcam and
  // will hand us their stream once it's ready (e.g. QuestionSection), so we
  // never race them into a second getUserMedia on the same device.
  openOwnCamera = true,
}) {
  const [ownStream, setOwnStream] = useState(null);
  const ownStreamRef = useRef(null);

  // Does the caller-supplied stream already carry a usable video track?
  const passedHasVideo =
    !!stream &&
    typeof stream.getVideoTracks === 'function' &&
    stream.getVideoTracks().some((t) => t && t.readyState !== 'ended');

  const effectiveStream = passedHasVideo ? stream : ownStream;

  // Open our own webcam ONLY when allowed AND the caller didn't hand us a
  // usable video stream.
  useEffect(() => {
    if (passedHasVideo || !openOwnCamera) return undefined;
    let cancelled = false;
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!md || typeof md.getUserMedia !== 'function') return undefined;

    md.getUserMedia({ video: true })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        ownStreamRef.current = s;
        setOwnStream(s);
      })
      .catch((e) => {
        // Camera denied / already in use / unavailable — proctoring simply
        // degrades; the test continues uninterrupted.
        // eslint-disable-next-line no-console
        console.warn('[proctoring] webcam unavailable for proctoring:', e?.message || e);
      });

    return () => {
      cancelled = true;
      try {
        ownStreamRef.current?.getTracks().forEach((t) => t.stop());
      } catch (_) {
        /* defensive */
      }
      ownStreamRef.current = null;
    };
  }, [passedHasVideo, openOwnCamera]);

  if (!testId) return null;

  return (
    <>
      <ProctoringEngine
        stream={effectiveStream}
        testId={testId}
        applicationId={applicationId}
        candidateEmail={candidateEmail}
        active={active}
      />
      <SnapshotEngine
        stream={effectiveStream}
        testId={testId}
        applicationId={applicationId}
        candidateEmail={candidateEmail}
        active={active}
      />
    </>
  );
}
